import {
    Activity,
    BarChart2,
    BookOpen,
    Box,
    Briefcase,
    Calendar,
    ClipboardList,
    Clock,
    CreditCard,
    FileSpreadsheet,
    FileText,
    GraduationCap,
    Heart,
    Home,
    LayoutDashboard,
    LogOut,
    Mail,
    MessageSquare,
    Package,
    Settings,
    Shield,
    Star,
    User,
    UserCircle,
    Users,
    Wallet
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { useSidebar } from '../../hooks/useSidebar';
import './Sidebar.css';

const Sidebar = () => {
    const { user, logout, hasRole, hasPermission } = useAuth();
    const { isOpen, setIsOpen } = useSidebar();
    const location = useLocation();
    const navigate = useNavigate();

    const [pendingSunatCount, setPendingSunatCount] = useState(0);

    useEffect(() => {
        const fetchPendingCount = async () => {
            try {
                if (hasPermission('CONTAB_BALANCE_VER') || hasRole('ADMIN') || hasRole('ADMINISTRADOR')) {
                    const response = await axios.get('/api/comprobantes/pendientes-count');
                    setPendingSunatCount(response.data.count || 0);
                } else {
                    setPendingSunatCount(0);
                }
            } catch (error) {
                console.error('Error fetching pending SUNAT count:', error);
            }
        };

        fetchPendingCount();

        window.addEventListener('sunat-pending-count-updated', fetchPendingCount);
        const interval = setInterval(fetchPendingCount, 300000); // 5 minutes

        return () => {
            clearInterval(interval);
            window.removeEventListener('sunat-pending-count-updated', fetchPendingCount);
        };
    }, [user, hasPermission, hasRole]);

    // Auto-close sidebar on mobile when navigating
    useEffect(() => {
        const handleRouteChange = () => {
            // Check if we are on mobile (screen width < 768px)
            if (window.innerWidth < 768) {
                setIsOpen(false);
            }
        };

        handleRouteChange();
    }, [location.pathname, setIsOpen]);

    // Helper roles
    const isTeacher = hasRole('DOCENTE') || hasRole('PROFESOR') || hasRole('AUXILIAR');
    const isAdmin = hasRole('ADMIN') || hasRole('ADMINISTRADOR');

    // Define sidebar structure with permissions
    const sidebarConfig = [
        {
            title: 'Inicio',
            icon: Home,
            to: '/inicio',
        },
        {
            title: 'Calendario',
            icon: Calendar,
            to: '/academico/calendario',
        },
        {
            title: 'Recursos Humanos',
            icon: Briefcase,
            permission: 'RRHH_VER',
            submenu: [
                { title: 'Lista de Personal', to: '/usuarios/personal', permission: 'RRHH_PERSONAL_VER' },
                { title: 'Gestión de Horarios', to: '/configuracion/horarios', permission: 'RRHH_HORARIOS_VER' },
                { title: 'Horarios de Personal', to: '/admin/horarios-personal', permission: 'RRHH_PERSONAL_VER' },
                { title: 'Plantillas y Contratos', to: '/documentos/plantillas', permission: 'DOCUMENTOS_CONTRATOS_VER' },
            ]
        },
        {
            title: 'Control de Asistencia',
            icon: Clock,
            permission: 'CONTROL_ASISTENCIA_VER',
            submenu: [
                { title: 'Control Facial (Cámara)', to: '/asistencia-facial', permission: 'ASISTENCIA_FACIAL_VER' },
                { title: 'Gestión Masiva por Aula', to: '/admin/asistencia-aula', permission: 'ASISTENCIA_SCANNER_VER' },
                { title: 'Scanner Asistencias', to: '/admin/asistencia-control', permission: 'ASISTENCIA_SCANNER_VER' },
                { title: 'Reporte Diario Detallado', to: '/admin/asistencia-reporte', permission: 'ASISTENCIA_SCANNER_VER' },
                { title: 'Justificar una Falta', to: '/admin/justificaciones', permission: 'ASISTENCIA_JUSTIFICAR_VER' },
                { title: 'Reporte de Asistencias', to: '/personal/reporte', isVisible: () => isAdmin },
            ]
        },
        {
            title: 'Gestión Académica',
            icon: GraduationCap,
            permission: 'GESTION_ACADEMICA_VER', // Parent permission
            isVisible: () => !isTeacher || isAdmin, // Hidden for pure teachers/professors
            submenu: [
                { title: 'Usuarios y Estudiantes', to: '/usuarios/lista', permission: 'USUARIOS_VER' },
                { title: 'Usuarios Estudiantes', to: '/usuarios/estudiantes', permission: 'ESTUDIANTES_VER' },
                { title: 'Usuarios Apoderados', to: '/usuarios/apoderados', permission: 'APODERADOS_VER' },
                { title: 'Matrícula', to: '/matriculas', permission: 'MATRICULA_VER' },
                { title: 'Gestión de Accesos', to: '/usuarios/gestion-accesos', permission: 'GESTION_ACCESOS_VER' },
                { title: 'Años Lectivos', to: '/anios-lectivos', permission: 'ANIOS_LECTIVOS_VER' },
                { title: 'Sedes', to: '/sedes', permission: 'SEDES_VER' },
                { title: 'Niveles', to: '/niveles', permission: 'NIVELES_VER' },
                { title: 'Grados', to: '/grados', permission: 'GRADOS_VER' },
                { title: 'Aulas', to: '/aulas', permission: 'AULAS_VER' },
                { title: 'Horarios de Aulas', to: '/academico/aulas-horarios', permission: 'AULAS_VER' },
                { title: 'Gestión de Unidades', to: '/academico/unidades', permission: 'UNIDADES_ACADEMICAS_VER' },
                { title: 'Regularización de Cursos', to: '/academico/asignar-cursos', permission: 'REGULARIZACION_CURSOS_VER' },
                { title: 'Vista Estructura (Beta)', to: '/academico/estructura', permission: 'ESTRUCTURA_ACADEMICA_VER' },
            ]
        },
        {
            title: 'Carnets',
            icon: CreditCard,
            permission: 'CARNETS_MODULO_VER',
            submenu: [
                { title: 'Auditoría de Carnets', to: '/admin/auditoria-carnets', permission: 'CARNETS_AUDITORIA_VER' },
                { title: 'Carnets de Personal', to: '/admin/auditoria-carnets-personal', permission: 'CARNETS_PERSONAL_VER' },
                { title: 'Estación de Producción', to: '/admin/lotes-carnets', permission: 'CARNETS_PRODUCCION_VER' },
                { title: 'Lotes de Personal', to: '/admin/lotes-carnets-personal', permission: 'CARNETS_PRODUCCION_VER' },
                { title: 'Quitar Fondo IA', to: '/admin/quitar-fondo', permission: 'CARNETS_IA_VER' },
                { title: 'Normalización de Nombres', to: '/usuarios/normalizacion', permission: 'CARNETS_NORMALIZACION_VER' },
            ]
        },
        {
            title: 'Mi Aula',
            icon: BookOpen,
            isVisible: () => isTeacher && !isAdmin,
            submenu: [
                { title: 'Mis Cursos', to: '/docente/mis-cursos', permission: 'DOCENTE_CURSOS_VER' },
                { title: 'Mi Horario', to: '/docente/mi-horario' },
                { title: 'Mis Alumnos', to: '/docente/mis-alumnos', permission: 'DOCENTE_ALUMNOS_VER' },
                { title: 'Material de Clases', to: '/academico/aula-docente', permission: 'DOCENTE_AULA_VER' },
                { title: 'Unidades Académicas', to: '/academico/unidades', permission: 'DOCENTE_UNIDADES_VER' },
                { title: 'Reportes Avanzados', to: '/docente/reportes-avanzados', permission: 'DOCENTE_AULA_VER' },
                { title: 'Descargar Plantillas (Excel)', to: '/docente/descargar-plantilla' },
                { title: 'Importación Masiva', to: '/docente/importar-masivo' },
                { title: 'Rellenar SIAGIE (Oficial)', to: '/docente/rellenar-siagie' },
                { title: 'Google Sheets en Vivo', to: '/docente/google-sheets' },
                { title: 'Méritos y Deméritos', to: '/academico/actitud', icon: Star, permission: 'DOCENTE_AULA_VER' },
            ]
        },
        {
            title: 'Reportes Director',
            icon: BarChart2,
            permission: 'SABANA_NOTAS_VER',
            isVisible: () => isAdmin,
            submenu: [
                { title: 'Notas Regular', to: '/academico/sabana-minedu?modalidad=1', permission: 'SABANA_NOTAS_VER', icon: ClipboardList },
                { title: 'Notas Verano', to: '/academico/sabana-minedu?modalidad=2', permission: 'SABANA_NOTAS_VER', icon: ClipboardList },
                { title: 'Notas Academia', to: '/academico/sabana-minedu?modalidad=3', permission: 'SABANA_NOTAS_VER', icon: ClipboardList },
                { title: 'Sábana de Notas', to: '/academico/sabana-notas', permission: 'SABANA_NOTAS_VER', icon: FileText },
                { title: 'Libreta de Notas', to: '/academico/libreta-notas', permission: 'LIBRETA_NOTAS_VER', icon: BookOpen },
                { title: 'Rellenar SIAGIE', to: '/academico/rellenar-siagie', permission: 'SABANA_NOTAS_VER', icon: FileSpreadsheet },
            ]
        },
        {
            title: 'Centro Académico',
            icon: Briefcase,
            isVisible: () => (hasPermission('MODULO_ACADEMICO_ADMIN_VER') || hasRole('AUXILIAR')) && !isTeacher,
            submenu: [
                { title: 'Panel Académico', to: '/academico/centro', permission: 'CENTRO_ACADEMICO_VER' },
                { title: 'Horarios de Clases', to: '/academico/horarios' },
                { title: 'Áreas Académicas', to: '/academico/areas', permission: 'AREAS_VER' },
                { title: 'Asignaturas', to: '/academico/asignaturas', permission: 'ASIGNATURAS_VER' },
                { title: 'Malla Curricular', to: '/academico/malla-curricular', permission: 'MALLA_VER' },
                { title: 'Gestión de Cursos', to: '/academico/cursos', permission: 'CURSOS_VER' },
                { title: 'Gestión de Evaluaciones', to: '/admin/gestion-evaluaciones', permission: 'MODULO_ACADEMICO_ADMIN_VER' },
                { title: 'Regularización de Cursos', to: '/academico/asignar-cursos', permission: 'REGULARIZACION_CURSOS_VER' },
                { title: 'Gestión de Alumnos', to: '/academico/alumnos', permission: 'ALUMNOS_VER' },
                { title: 'Competencias MINEDU', to: '/academico/competencias', permission: 'COMPETENCIAS_MINEDU_VER' },
                { title: 'Tipos de Actividad', to: '/academico/tipos-actividad', permission: 'TIPOS_ACTIVIDAD_VER' },
                { title: 'Unidades Académicas', to: '/academico/unidades', permission: 'UNIDADES_ACADEMICAS_VER' },
                { title: 'Descargar Plantillas (Excel)', to: '/docente/descargar-plantilla' },
                { title: 'Importación de Notas (Masiva)', to: '/docente/importar-masivo' },
                { title: 'Hojas en Vivo (Google Sheets)', to: '/academico/google-sheets' },
                { title: 'Aula Virtual Docente', to: '/academico/aula-docente', permission: 'AULA_VIRTUAL_DOCENTE_VER' },
                { title: 'Portal del Alumno', to: '/academico/mi-aula', permission: 'PORTAL_ALUMNO_VER' },
                { title: 'Sábana de Notas', to: '/academico/sabana-notas', permission: 'SABANA_NOTAS_VER' },
                { title: 'Notas Regular', to: '/academico/sabana-minedu?modalidad=1', permission: 'SABANA_NOTAS_VER' },
                { title: 'Notas Verano', to: '/academico/sabana-minedu?modalidad=2', permission: 'SABANA_NOTAS_VER' },
                { title: 'Notas Academia', to: '/academico/sabana-minedu?modalidad=3', permission: 'SABANA_NOTAS_VER' },
                { title: 'Libreta de Notas', to: '/academico/libreta-notas', permission: 'LIBRETA_NOTAS_VER' },
                { title: 'Rellenar SIAGIE (Oficial)', to: '/academico/rellenar-siagie', permission: 'SABANA_NOTAS_VER' },
                { title: 'Movimiento de Alumnos', to: '/academico/movimiento-alumnos', permission: 'ALUMNOS_VER' },
                { title: 'Méritos y Deméritos', to: '/academico/actitud', icon: Star, permission: 'ALUMNOS_VER' },
                { title: 'Códigos de Barra', to: '/academico/barcodes', permission: 'BARCODES_VER' },
            ]
        },

        {
            title: 'Caja',
            icon: Wallet,
            permission: 'CAJA_VER',
            submenu: [
                { title: 'Gestión de Caja', to: '/caja/gestion', permission: 'CAJA_GESTION_VER' },
                { title: 'Caja Chica', to: '/caja/chica', permission: 'CAJA_CHICA_VER' },
                { title: 'Caja Verano', to: '/caja/verano', permission: 'CAJA_VERANO_VER' },
                { title: 'Caja Academia', to: '/caja/academia', permission: 'CAJA_ACADEMIA_VER' },
                { title: 'Caja Oficina', to: '/caja/oficina', permission: 'CAJA_OFICINA_VER' },
                { title: 'Cajas Chicas Admin', to: '/caja/admin', permission: 'CAJA_ADMIN_VER' },
                { title: 'Administración Global', to: '/caja/administracion', permission: 'CAJA_GLOBAL_VER' },
                { title: 'Gastos / Egresos', to: '/caja/gastos', permission: 'CAJA_EGRESOS_VER' },
                { title: 'Recaudación Libre', to: '/caja/recaudacion-libre', permission: 'CAJA_RECAUDACION_LIBRE_VER' },
                { title: 'Cronogramas de Pago', to: '/caja/cronogramas', permission: 'CRONOGRAMAS_VER' },
                { title: 'Gestión de Comprobantes', to: '/comprobantes', permission: 'COMPROBANTES_VER' },
                { title: 'Reporte Recaudación', to: '/caja/recaudacion', permission: 'RECAUDACION_REPORTE_VER' },
                { title: 'Config. Cuentas', to: '/caja/cuentas', permission: 'CAJA_CONFIG_VER' },
                { title: 'Config. Pagos', to: '/configuracion-pagos', permission: 'PAGOS_CONFIG_VER' },
            ]
        },
        {
            title: 'Uniformes',
            icon: Package,
            permission: 'UNIFORMES_VER',
            submenu: [
                { title: 'Punto de Venta', to: '/uniformes/venta', permission: 'UNIFORMES_VENTA_VER' },
                { title: 'Inventario', to: '/uniformes/inventario', permission: 'UNIFORMES_INV_VER' },
                { title: 'Auditoría de Stock', to: '/uniformes/auditoria', permission: 'UNIFORMES_INV_VER' },
                { title: 'Auditoría Financiera', to: '/uniformes/auditoria-caja', permission: 'UNIFORMES_CAJA_VER' },
                { title: 'Pedidos al Proveedor', to: '/uniformes/compras', permission: 'UNIFORMES_INV_VER' },
                { title: 'Pedidos Especiales', to: '/uniformes/pedidos', permission: 'UNIFORMES_PEDIDOS_VER' },
                { title: 'Caja Uniformes', to: '/caja/uniformes', permission: 'UNIFORMES_CAJA_VER' },
            ]
        },
        {
            title: 'Bienes Patrimoniales',
            icon: Box,
            permission: 'INVENTARIO_BIENES_VER',
            submenu: [
                { title: 'Inventario de Bienes', to: '/inventario/bienes', permission: 'INVENTARIO_BIENES_VER' }
            ]
        },
        {
            title: 'Contabilidad',
            icon: LayoutDashboard,
            permission: 'CONTABILIDAD_VER',
            submenu: [
                { title: 'Reporte Ingresos', to: '/contabilidad/ingresos', permission: 'CONTAB_INGRESOS_VER' },
                { title: 'Reporte Egresos', to: '/contabilidad/egresos', permission: 'CONTAB_EGRESOS_VER' },
                { title: 'Balance General', to: '/contabilidad/balance', permission: 'CONTAB_BALANCE_VER' },
                { title: 'Facturación SUNAT', to: '/contabilidad/sunat', permission: 'CONTAB_BALANCE_VER' },
            ]
        },
        {
            title: 'Psicología',
            icon: Heart,
            permission: 'PSICO_VER',
            submenu: [
                { title: 'Mis Citas', to: '/psicologia/citas', permission: 'PSICO_CITAS_VER' },
                { title: 'Horarios Admisión', to: '/psicologia/disponibilidad-admisiones', permission: 'PSICO_ADMISION_VER' },
                { title: 'Plantillas', to: '/psicologia/plantillas', permission: 'PSICO_PLANTILLAS_VER' },
                { title: 'Horario', to: '/psicologia/horario', permission: 'PSICO_HORARIO_VER' },
                { title: 'Directorio Estudiantil', to: '/psicologia/directorio', permission: 'PSICO_VER' },
            ]
        },

        {
            title: 'Configuración',
            icon: Settings,
            permission: 'CONFIGURACION_VER',
            submenu: [
                // Moved to RR.HH.
            ]
        },
        {
            title: 'Seguridad',
            icon: Shield,
            permission: 'SEGURIDAD_VER',
            submenu: [
                { title: 'Roles y Permisos', to: '/seguridad/roles', permission: 'ROLES_VER' },
                { title: 'Contraseñas de Usuarios', to: '/admin/gestion-passwords', isVisible: () => isAdmin }
            ]
        },
        {
            title: 'Mi Área (Personal)',
            icon: User,
            submenu: [
                { title: 'Mi Horario', to: '/personal/horario' },
                { title: 'Mi Asistencia', to: '/personal/asistencia' }
            ],
            // Show if user is staff (but not just student/parent)
            isVisible: (user) => {
                if (!user) return false;
                const isOnlyStudentOrParent = user.roles?.every(r => {
                    const name = typeof r === 'string' ? r : r.nombre;
                    return name === 'ESTUDIANTE' || name === 'APODERADO';
                });
                return !isOnlyStudentOrParent || !!user.tipoPersonal;
            }
        },
        {
            title: 'Familia Orion',
            icon: Users,
            isVisible: (user) => hasRole('APODERADO') || !!user?.apoderado_info,
            submenu: [
                { title: 'Asistencia', to: '/apoderado/asistencia' },
                { title: 'Notas', to: '/apoderado/notas' },
                { title: 'Conducta', to: '/apoderado/actitud' },
                { title: 'Psicología', to: '/apoderado/psicologia' },
                { title: 'Pagos', to: '/apoderado/pagos' },
                { title: 'Datos de Hijos', to: '/apoderado/datos-hijos' },
                { title: 'Orion Estudiantil', to: '/apoderado/orion-estudiantil' }
            ]
        },
        {
            title: 'Orion Estudiantil',
            icon: UserCircle,
            role: 'ESTUDIANTE',
            submenu: [
                { title: 'Mis Cursos', to: '/alumno/mis-cursos', role: 'ESTUDIANTE' },
                { title: 'Unidades Académicas', to: '/academico/unidades', role: 'ESTUDIANTE' },
                { title: 'Mis Calificaciones', to: '/alumno/mis-calificaciones', role: 'ESTUDIANTE' },
                { title: 'Mis Exámenes', to: '/alumno/mis-examenes', role: 'ESTUDIANTE' },
                { title: 'Mi Asistencia', to: '/alumno/mi-asistencia', role: 'ESTUDIANTE' },
                { title: 'Mi Horario', to: '/alumno/mi-horario', role: 'ESTUDIANTE' }
            ]
        },
        {
            title: 'Depuración n8n',
            icon: Activity,
            to: '/admin/n8n-debug',
            permission: 'WHATSAPP_TEST_VER'
        },
        {
            title: 'Prueba WhatsApp',
            icon: MessageSquare,
            to: '/whatsapp-test',
            permission: 'WHATSAPP_TEST_VER'
        }
    ];

    // Helper to check visibility
    const isVisible = (item) => {
        // If user is Admin, show everything (shortcut)
        if (hasRole('ADMIN') || hasRole('ADMINISTRADOR')) return true;

        // If item has a custom isVisible function, use it
        if (typeof item.isVisible === 'function') {
            return item.isVisible(user);
        }

        // If item has a role requirement, check it
        if (item.role) {
            return hasRole(item.role);
        }

        // If item has a permission requirement, check it
        if (item.permission) {
            // Special case for Psychology: Keep existing logic for backward compatibility
            if (item.title === 'Psicología' && (hasRole('PSICOLOGA') || hasRole('PSICOLOGIA'))) return true;

            // Check if user has explicit permission
            if (hasPermission(item.permission)) return true;

            // SMART FOLDER: If it's a folder (has submenu) and user doesn't have parent permission,
            // check if they can see at least one child.
            if (item.submenu) {
                return item.submenu.some(sub => {
                    if (sub.separator) return false;
                    if (sub.isVisible && sub.isVisible(user)) return true;
                    if (sub.role && hasRole(sub.role)) return true;
                    if (sub.permission && hasPermission(sub.permission)) return true;
                    // If no restrictions on child, it would be visible normally
                    if (!sub.permission && !sub.role && !sub.isVisible) return true;
                    return false;
                });
            }

            return false;
        }

        // If no permission specified, show it (like Dashboard or Home)
        return true;
    };

    const [activeSubmenu, setActiveSubmenu] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [submenuPositions, setSubmenuPositions] = useState({});

    // Al compactar el sidebar, cerrar cualquier acordeón abierto para que no quede estirado
    useEffect(() => {
        if (!isOpen) {
            setActiveSubmenu('');
        }
    }, [isOpen]);

    const openProfileModal = (e) => {
        e.preventDefault();
        navigate('/perfil');
        setIsDropdownOpen(false);
    };

    const handleSubmenuToggle = (title, e) => {
        e.preventDefault();
        e.stopPropagation(); // Evitar que plugins de jQuery (MetisMenu) interfieran con el click manejado por React
        if (!isOpen) {
            // En modo comprimido nunca se estira hacia abajo: sólo se usa el emergente
            return;
        }
        setActiveSubmenu(activeSubmenu === title ? '' : title);
    };

    const toggleDropdown = (e) => {
        e.preventDefault();
        setIsDropdownOpen(!isDropdownOpen);
    };

    // Handler para calcular la posición del submenú emergente en modo comprimido
    const handleMenuHover = useCallback((e, itemTitle) => {
        const mainWrapper = document.getElementById('main-wrapper');
        const isCompressed = mainWrapper?.classList.contains('menu-toggle');

        if (isCompressed) {
            const liElement = e.currentTarget;
            const rect = liElement.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            let top = rect.top;
            // Evitar que el emergente se desborde por la parte inferior de la pantalla
            if (top + 280 > windowHeight) {
                top = Math.max(10, windowHeight - 290);
            }
            setSubmenuPositions(prev => ({
                ...prev,
                [itemTitle]: top
            }));
        }
    }, []);

    return (
        <div className="dlabnav">
            <div className="dlabnav-scroll">
                <ul className="metismenu" id="menu">
                    <li className={`dropdown header-profile ${isDropdownOpen ? 'show' : ''}`}>
                        <a
                            className="nav-link"
                            href="#"
                            role="button"
                            onClick={toggleDropdown}
                            aria-expanded={isDropdownOpen}
                            style={{
                                padding: '0.4rem 0.65rem',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {user?.foto_url ? (
                                <img
                                    src={user.foto_url.startsWith('/uploads') ? user.foto_url : `/uploads/${user.foto_url}`}
                                    alt="Perfil"
                                    style={{ borderRadius: '50%', objectFit: 'cover', width: '32px', height: '32px', flexShrink: 0 }}
                                />
                            ) : (
                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <User size={18} className="text-secondary" />
                                </div>
                            )}
                            <div className="header-info ms-2 d-flex align-items-center justify-content-between flex-grow-1" style={{ minWidth: 0 }}>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <span className="font-w600 d-block" style={{ fontSize: '0.82rem', lineHeight: '1.2' }}>
                                        {user?.firstName || (user?.realName ? (user.realName.split(' ')[2] || user.realName.split(' ')[0]) : (user?.username || 'Usuario'))}
                                    </span>
                                    <small className="font-w400" style={{ fontSize: '0.66rem', opacity: 0.65, display: 'block', lineHeight: '1.2' }}>
                                        {user?.roles && user.roles.length > 0 ? user.roles[0].nombre : (user?.tipoPersonal || 'Usuario')}
                                    </small>
                                </div>
                                <Settings size={15} className="text-muted opacity-50 ms-auto" style={{ flexShrink: 0 }} />
                            </div>
                        </a>
                        <div className={`dropdown-menu dropdown-menu-end ${isDropdownOpen ? 'show' : ''}`}>
                            <a href="#" className="dropdown-item ai-icon" onClick={openProfileModal}>
                                <User size={18} className="text-primary" />
                                <span className="ms-2">Perfil </span>
                            </a>
                            <Link to="/email-inbox" className="dropdown-item ai-icon">
                                <Mail size={18} className="text-success" />
                                <span className="ms-2">Bandeja </span>
                            </Link>
                            <button onClick={logout} className="dropdown-item ai-icon" style={{ border: 'none', background: 'none', width: '100%', textAlign: 'left' }}>
                                <LogOut size={18} className="text-danger" />
                                <span className="ms-2">Cerrar Sesión </span>
                            </button>
                        </div>
                    </li>

                    {sidebarConfig.map((item, index) => {
                        if (!isVisible(item)) return null;

                        if (item.submenu) {
                            // Filter submenu items
                            const visibleSubmenu = item.submenu.filter(sub => isVisible(sub));
                            if (visibleSubmenu.length === 0) return null;

                            const isActive = activeSubmenu === item.title;

                            return (
                                <li
                                    key={index}
                                    className={`has-sub ${isActive ? 'mm-active' : ''}`}
                                    onMouseEnter={(e) => handleMenuHover(e, item.title)}
                                >
                                    <a
                                        className="has-arrow ai-icon"
                                        href="#"
                                        aria-expanded={isActive}
                                        onClick={(e) => handleSubmenuToggle(item.title, e)}
                                    >
                                        <div className="menu-icon">
                                            {typeof item.icon === 'string' ? <i className={item.icon}></i> : <item.icon size={18} strokeWidth={1.8} />}
                                        </div>
                                        <span className="nav-text">
                                            {item.title}
                                            {item.title === 'Contabilidad' && pendingSunatCount > 0 && (
                                                <span className="badge badge-xs badge-danger ms-2" style={{
                                                    borderRadius: '10px',
                                                    fontSize: '0.7rem',
                                                    padding: '3px 6px',
                                                    backgroundColor: '#dc3545',
                                                    color: '#fff',
                                                    fontWeight: 'bold',
                                                    verticalAlign: 'middle',
                                                    display: 'inline-block'
                                                }}>
                                                    {pendingSunatCount}
                                                </span>
                                            )}
                                        </span>
                                    </a>
                                    <ul
                                        aria-expanded={isActive}
                                        className={`${isActive ? 'mm-show' : 'mm-collapse'}`}
                                        style={submenuPositions[item.title] ? { '--submenu-top': `${submenuPositions[item.title]}px` } : {}}
                                    >
                                        {visibleSubmenu.map((sub, subIndex) => {
                                            // Handle separator items
                                            if (sub.separator) {
                                                if (sub.isVisible && !sub.isVisible()) return null;
                                                return (
                                                    <li key={subIndex} className="menu-separator">
                                                        <span style={{
                                                            fontSize: '0.75rem',
                                                            color: '#999',
                                                            fontWeight: '600',
                                                            padding: '0.5rem 1rem',
                                                            display: 'block'
                                                        }}>
                                                            {sub.title}
                                                        </span>
                                                    </li>
                                                );
                                            }
                                            return (
                                                <li key={subIndex}>
                                                    <Link to={sub.to}>
                                                        {sub.title}
                                                        {sub.title === 'Facturación SUNAT' && pendingSunatCount > 0 && (
                                                            <span className="badge badge-xs badge-danger ms-2" style={{
                                                                borderRadius: '10px',
                                                                fontSize: '0.65rem',
                                                                padding: '2px 6px',
                                                                backgroundColor: '#dc3545',
                                                                color: '#fff',
                                                                fontWeight: 'bold',
                                                                verticalAlign: 'middle',
                                                                display: 'inline-block'
                                                            }}>
                                                                {pendingSunatCount}
                                                            </span>
                                                        )}
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </li>
                            );
                        } else {
                            return (
                                <li key={index}>
                                    <Link to={item.to} className="ai-icon" aria-expanded="false">
                                        <div className="menu-icon">
                                            {typeof item.icon === 'string' ? <i className={item.icon}></i> : <item.icon size={18} strokeWidth={1.8} />}
                                        </div>
                                        <span className="nav-text">{item.title}</span>
                                    </Link>
                                </li>
                            );
                        }
                    })}

                </ul>
                <div className="copyright" style={{ marginLeft: 0, marginRight: 0, paddingTop: 0 }}>
                    <p><strong>Colegio Orion Internacional</strong> © 2025 Todos los derechos reservados</p>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
